<script setup lang="ts">
import { defaultBadgeConfig, normalizeBadge, TYPOGRAPHY_BADGE_FRAME, withCurrentCurves } from './config';
import { typographyPolicy, typographyPresets } from './policy';
import type { TypographyBadgeConfig, TypographyDesignItem } from './types';
import { computed, ref } from 'vue';
import { t } from './i18n';
import { ensureItemFonts } from './fontEngine';
import TypographyPresetPreview from './TypographyPresetPreview.vue';

const badgePresets = computed(() => typographyPresets.value.filter((preset) => preset.kind === 'badge' && (typographyPolicy.value.allowedPresetIds.length === 0 || typographyPolicy.value.allowedPresetIds.includes(preset.id))));
const query = ref('');
const visiblePresets = computed(() => {
  const term = query.value.trim().toLowerCase();
  return term ? badgePresets.value.filter((preset) => preset.title.toLowerCase().includes(term)) : badgePresets.value;
});

function addBadge(config: TypographyBadgeConfig = defaultBadgeConfig()) {
  const Veyop = window.Veyop;
  const commands = Veyop?.designer?.commands;
  if (!commands) return;
  const width = 320;
  const height = Math.round(width * TYPOGRAPHY_BADGE_FRAME.height / TYPOGRAPHY_BADGE_FRAME.width);
  const safeConfig = withCurrentCurves(normalizeBadge(config));
  const effects = typographyPolicy.value.allowedEffects;
  const maxChars = typographyPolicy.value.maxChars;
  safeConfig.top.text = safeConfig.top.text.slice(0, maxChars);
  safeConfig.bottom.text = safeConfig.bottom.text.slice(0, maxChars);
  safeConfig.center.text = safeConfig.center.text.slice(0, maxChars);
  if (!effects.includes('gradient')) safeConfig.appearance.fill.type = 'solid';
  if (!effects.includes('outline')) safeConfig.appearance.stroke.enabled = false;
  if (!effects.includes('shadow')) safeConfig.appearance.shadow.enabled = false;
  if (!effects.includes('extrude')) safeConfig.appearance.extrude.enabled = false;
  const id = `typography-badge-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const item: TypographyDesignItem = { id, type: 'shape', top: 0, left: 0, width, height, angle: 0, zIndex: Date.now() % 100000, typographyBadge: safeConfig, typographyFontReady: false, typographyFrame: { ...TYPOGRAPHY_BADGE_FRAME }, typographyScale: width / TYPOGRAPHY_BADGE_FRAME.width };
  commands.addItem(item as never, { placement: 'print-area-center' });
  void ensureItemFonts(item).then((ready) => {
    if (!ready) return;
    commands.updateItem(id, { typographyFontReady: true });
  });
}
</script>

<template>
  <section class="vptw:text-(--vs-ink)">
    <div
      class="vptw:rounded-2xl vptw:p-4 vptw:text-white vptw:shadow-sm" style="background: var(--vs-accent-grad, #0071e3)">
      <p class="vptw:m-0 vptw:text-[10px] vptw:font-bold vptw:uppercase vptw:tracking-wider vptw:text-white">
        {{ t('Typography Pro') }}</p>
      <h3 class="vptw:m-0 vptw:mt-1 vptw:text-base vptw:font-semibold">{{ t('Create a badge') }}</h3>
      <p class="vptw:m-0 vptw:mt-1 vptw:text-xs vptw:text-white">{{ t('Choose a ready-made look or start with your own words.') }}
      </p><button type="button"
        class="vptw:mt-4 vptw:w-full vptw:rounded-xl vptw:bg-(--vs-surface)! vptw:px-4 vptw:py-3 vptw:font-semibold vptw:text-(--vs-accent)! vptw:shadow-sm vptw:cursor-pointer vptw:hover:bg-(--vs-accent-soft)! vptw:focus-visible:outline-none vptw:focus-visible:ring-2 vptw:focus-visible:ring-white"
        @click="addBadge()">+ {{ t('Start from scratch') }}</button>
    </div>
    <div v-if="badgePresets.length" class="vptw:mt-4">
      <div class="vptw:flex vptw:items-center vptw:justify-between">
        <h4 class="vptw:m-0 vptw:text-xs vptw:font-semibold">{{ t('Ready-made badges') }}</h4><span
          class="vptw:rounded-full vptw:bg-(--vs-surface-2) vptw:px-2 vptw:py-1 vptw:text-[10px] vptw:text-(--vs-ink-2)">{{ visiblePresets.length }}</span>
      </div><input v-model="query" type="search" :placeholder="t('Search badges…')"
        class="vptw:mt-2 vptw:w-full vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:px-3 vptw:py-2 vptw:text-xs">
      <div class="vptw:mt-3 vptw:grid vptw:grid-cols-2 vptw:gap-2"><button v-for="preset in visiblePresets"
          :key="preset.id" type="button" :title="preset.title"
          class="vptw:min-w-0 vptw:overflow-hidden vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-(--vs-surface) vptw:p-2 vptw:text-left vptw:shadow-sm vptw:transition vptw:hover:-translate-y-0.5 vptw:hover:border-(--vs-accent) vptw:hover:shadow-md vptw:focus-visible:outline-none vptw:focus-visible:ring-2 vptw:focus-visible:ring-(--vs-accent)"
          @click="addBadge(preset.config as TypographyBadgeConfig)">
          <TypographyPresetPreview kind="badge" :config="preset.config as Record<string, unknown>" :title="preset.title"
            compact /><span class="vptw:block vptw:min-w-0 vptw:truncate vptw:pt-2 vptw:text-xs vptw:font-semibold">{{
              preset.title }}</span>
        </button></div>
      <p v-if="!visiblePresets.length"
        class="vptw:rounded-xl vptw:bg-(--vs-surface-2) vptw:p-4 vptw:text-center vptw:text-xs vptw:text-(--vs-ink-2)">{{ t('No matching badges.') }}</p>
    </div>
  </section>
</template>
